import { DatabaseBackup, FolderDown, HardDriveDownload, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { Input } from "@/components/ui/primitives";
import { api } from "@/lib/api";

interface OpsStatus {
  desktop: boolean;
  data_dir: string;
  vault_dir: string | null;
  backup_dir: string;
  last_backup_at: string | null;
}

/** Backup settings: where snapshots go, and a one-click "Back up now". The
 * local store stays primary — pointing the folder at a path your OneDrive /
 * Dropbox / Drive client already syncs is the whole "optional cloud backup":
 * no cloud code, the synced .zip is the off-device copy. */
export function BackupDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [status, setStatus] = useState<OpsStatus | null>(null);
  const [dir, setDir] = useState("");
  const [saving, setSaving] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const refresh = async () => {
    try {
      const s = await api.request<OpsStatus>("GET", "/api/ops/status");
      setStatus(s);
      setDir(s.backup_dir);
    } catch {
      /* unavailable (older backend / not signed in) — leave empty */
    }
  };

  useEffect(() => {
    if (open) void refresh();
  }, [open]);

  const save = async () => {
    if (!dir.trim()) return;
    setSaving(true);
    try {
      const r = await api.request<{ backup_dir: string }>("PUT", "/api/ops/backup-dir", {
        path: dir.trim(),
      });
      setDir(r.backup_dir);
      setStatus((s) => (s ? { ...s, backup_dir: r.backup_dir } : s));
      toast.success("Backup folder set", { description: r.backup_dir });
    } catch (e) {
      toast.error("Couldn't set that folder", { description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const backupNow = async () => {
    setBackingUp(true);
    try {
      const r = await api.request<{ archive: string }>("POST", "/api/ops/backup");
      toast.success("Backup saved", { description: r.archive });
      await refresh();
    } catch (e) {
      toast.error("Backup failed", { description: String(e) });
    } finally {
      setBackingUp(false);
    }
  };

  const last = status?.last_backup_at ? new Date(status.last_backup_at) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Backups" className="w-[min(620px,92vw)]">
        <div className="space-y-4">
          <p className="text-xs leading-relaxed text-muted-foreground">
            Your atlas lives on this device. A backup is a single zipped snapshot —
            database, files, and your vault — written to the folder below.{" "}
            <span className="text-foreground">
              Point it at a folder your OneDrive, Dropbox, or Google Drive already syncs
            </span>{" "}
            and the snapshot is copied to the cloud automatically — an opt-in safety net,
            never your primary store.
          </p>

          <div>
            <label className="mb-1.5 flex items-center gap-1.5 font-mono text-2xs uppercase tracking-wide text-muted-foreground">
              <FolderDown className="h-3.5 w-3.5" /> Backup folder
            </label>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <Input
                value={dir}
                onChange={(e) => setDir(e.target.value)}
                placeholder="e.g. C:\Users\you\OneDrive\GraphView Backups"
                aria-label="Backup folder path"
              />
              <Button type="submit" variant="ghost" disabled={!dir.trim() || saving}>
                <Save className="h-3.5 w-3.5" />
                {saving ? "Saving…" : "Save"}
              </Button>
            </form>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-background p-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[12.5px] text-foreground">
                <DatabaseBackup className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                {last ? `Last backup ${last.toLocaleString()}` : "No backup yet"}
              </p>
            </div>
            <Button variant="default" disabled={backingUp} onClick={() => void backupNow()}>
              <HardDriveDownload className="h-3.5 w-3.5" />
              {backingUp ? "Backing up…" : "Back up now"}
            </Button>
          </div>

          {status && (
            <dl className="space-y-1.5 font-mono text-2xs text-muted-foreground">
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 uppercase tracking-wide">Data</dt>
                <dd className="truncate text-muted-foreground">{status.data_dir}</dd>
              </div>
              {status.vault_dir && (
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 uppercase tracking-wide">Vault</dt>
                  <dd className="truncate text-muted-foreground">{status.vault_dir}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
